import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApi, onPath, onKeys, unwrap } from '../_shared';

export interface ScheduledAction {
  id: string;
  parent_id?: string;
  type?: string;
  /** The action to perform: backup, poweroff, reboot, disk-snapshot-create, … */
  schedule_action?: string;
  /** Start time as a unix epoch string. */
  time?: string;
  /** Repeat code: 0 weekly · 1 monthly · 2 yearly · 3 hourly (empty = one-time). */
  repeat?: string;
  days?: string | null;
  /** End code: 0 never · 1 after N repetitions · 2 on date. */
  end_type?: string;
  end_value?: string;
  done?: string;
  message?: string | null;
  args?: string | null;
  warning?: string;
}

/** Payload for create/update of a scheduled action (matches the s10003 API). */
export interface ScheduledActionPayload {
  schedule_action: string;
  /** unix epoch seconds */
  time: number;
  repeat?: number;
  days?: string;
  end_type: number;
  end_value: string | number;
  args?: string;
}

export function useScheduledActions(serviceId: string | undefined, instanceId: string | undefined) {
  return useQuery({
    queryKey: onKeys.scheduledActions(serviceId ?? '', instanceId ?? ''),
    enabled: !!serviceId && !!instanceId,
    retry: false,
    queryFn: async () => {
      const body = unwrap<{ schedules?: ScheduledAction[] } | ScheduledAction[]>(
        await serviceApi.get(onPath(serviceId!, `instances/${instanceId}/schedules`)),
      );
      return Array.isArray(body) ? body : body.schedules ?? [];
    },
  });
}

export function useCreateScheduledAction(
  serviceId: string,
  instanceId: string,
  options?: UseMutationOptions<unknown, unknown, ScheduledActionPayload>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ScheduledActionPayload) =>
      (await serviceApi.post(onPath(serviceId, `instances/${instanceId}/schedules`), payload)).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.scheduledActions(serviceId, instanceId) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useUpdateScheduledAction(
  serviceId: string,
  instanceId: string,
  options?: UseMutationOptions<unknown, unknown, { id: string; payload: Partial<ScheduledActionPayload> }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }) =>
      (await serviceApi.put(onPath(serviceId, `instances/${instanceId}/schedules/${id}`), payload)).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.scheduledActions(serviceId, instanceId) });
      options?.onSuccess?.(...a);
    },
  });
}

export function useDeleteScheduledAction(
  serviceId: string,
  instanceId: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (actionId: string) =>
      (await serviceApi.delete(onPath(serviceId, `instances/${instanceId}/schedules/${actionId}`))).data,
    ...options,
    onSuccess: (...a) => {
      qc.invalidateQueries({ queryKey: onKeys.scheduledActions(serviceId, instanceId) });
      options?.onSuccess?.(...a);
    },
  });
}
